import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { newsletterService } from './newsletter.service';
import { formatDateTime } from '../../helpers';
import type { Newsletter } from './newsletter.dto';

const NewsletterPage = () => {
  const { token, isAuthenticated, loading: authLoading, logout } = useAuth();
  const { showSuccess, showError } = useToast();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Tracked but not currently displayed anywhere in this page's UI.
  const [, setTotalNewsletters] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newsletterToDelete, setNewsletterToDelete] = useState<number | null>(
    null
  );
  const itemsPerPage = 10;

  // Set up logout callback for session expired
  useEffect(() => {
    newsletterService.setLogoutCallback(logout);
  }, [logout]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Fetch newsletters from API
  const fetchNewsletters = useCallback(async () => {
    if (!token || !isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await newsletterService.getNewsletters(token, {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm || undefined,
      });

      setNewsletters(response.data || []);
      setTotalPages(response.meta?.lastPage || 1);
      setTotalNewsletters(response.meta?.total || 0);
    } catch (err) {
      console.error('Error fetching newsletters:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch newsletter subscriptions'
      );
      setNewsletters([]);
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated, currentPage, debouncedSearchTerm, itemsPerPage]);

  useEffect(() => {
    fetchNewsletters();
  }, [fetchNewsletters]);

  // Handlers
  const handleDeleteClick = (id: number) => {
    setNewsletterToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!token || !newsletterToDelete) return;

    try {
      await newsletterService.deleteNewsletter(token, newsletterToDelete);
      showSuccess('Newsletter subscription deleted successfully');
      setDeleteDialogOpen(false);
      setNewsletterToDelete(null);

      // If current page becomes empty after delete, go to previous page
      if (newsletters.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchNewsletters();
      }
    } catch (err) {
      console.error('Error deleting newsletter subscription:', err);
      showError(
        err instanceof Error
          ? err.message
          : 'Failed to delete newsletter subscription'
      );
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setNewsletterToDelete(null);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  // Loading state
  if (authLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Render
  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h4">Newsletter Subscriptions</Typography>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by email..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
            endAdornment: (
              <Box display="flex" alignItems="center" gap={1}>
                {searchTerm !== debouncedSearchTerm && (
                  <CircularProgress size={20} />
                )}
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    disabled={loading}
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ),
          }}
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Newsletters Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Subscribed Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading newsletter subscriptions...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : newsletters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm
                        ? 'No newsletter subscriptions found matching your search'
                        : 'No newsletter subscriptions found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                newsletters.map((newsletter, index) => (
                  <TableRow key={newsletter.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {newsletter.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(newsletter.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(newsletter.id)}
                        size="small"
                        disabled={loading}
                        title="Delete Subscription"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this newsletter subscription? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewsletterPage;
